/**
 * Uni Forms — "get responses" screens.
 *
 * The builder lists (``UniFormsPages.jsx``) let you create and edit a
 * form, quiz or poll.  These three pages are where you go afterwards to
 * read what came back:
 *
 *   * UniFormsFormResponses — one row per submission, one column per
 *     question, click a row to see the whole answer sheet.
 *   * UniFormsQuizResponses — one row per attempt, with a score /
 *     pass-rate summary above the table.
 *   * UniFormsPollResults   — the tally, as bars (a poll has no
 *     per-respondent detail worth paging through).
 *
 * Why per-object endpoints
 * ------------------------
 * Each page reads ``/api/uni-forms/<collection>/<id>/responses/`` (or
 * ``/results/``) rather than filtering the flat ``/responses/`` list.
 * The nested route is where the access check lives — the server decides
 * whether *this* user may read *this* form's submissions and 403s
 * otherwise (``api_permissions.can_view_responses``).  It also returns
 * the question columns and the aggregate totals next to the rows, which
 * a client holding one page of results cannot compute for itself.
 *
 * Export is a plain link to the ``…/export/`` route (streamed CSV via
 * ``downloadFile`` so the JWT header is attached — a bare <a href> would
 * be an unauthenticated request and bounce to /login).
 */
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { downloadFile, getJSON, postJSON } from '../../api/client.js';
import {
  fmtCount, UniActionButton, UniBool, UniDetailRow, UniFormError, UniKpiCard, UniModal,
  UniPageShell, UniPager, UniSection, UniStatusPill, UniTable, UniToolbar,
} from './UniShared.jsx';
import { ROUTES } from '../../config.js';
import { Field } from './UniFormsPublicFill.jsx';
import { useLiveCalculations } from './useLiveCalculations.js';
import './uniPublicForm.css';

import { formatInTimeZone, useOrgTimeZone } from '../../lib/displayTimezone.js';

const THEME = 'forms';

const EP = {
  FORMS:   '/api/uni-forms/forms/',
  QUIZZES: '/api/uni-forms/quizzes/',
  POLLS:   '/api/uni-forms/polls/',
};


/** Date + time in the organisation timezone, or an em-dash. */
function fmtDateTime(value, timeZone) {
  if (!value) return '—';
  return formatInTimeZone(value, timeZone);
}


/* ====================================================================
   Shared fetch hook for the nested response endpoints
   ====================================================================
   Not ``useUniList``: these endpoints return extra top-level keys
   (``columns``, ``summary``, ``form_title``) alongside ``results``, and
   ``useUniList`` throws everything except the rows away.  A 403 here is
   also meaningful — "you may not read these submissions" — so it's
   surfaced as its own state rather than a generic load error.
   */
function useResponses(url) {
  const [state, setState] = React.useState({
    data: null, loading: true, error: null,
  });

  const load = React.useCallback(() => {
    if (!url) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    getJSON(url)
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((error) => setState({ data: null, loading: false, error }));
  }, [url]);

  React.useEffect(load, [load]);

  const forbidden = state.error?.status === 403
    || /HTTP 403/.test(String(state.error?.message || ''));

  return { ...state, forbidden, reload: load };
}


/** Panel shown instead of the table when the server says 403. */
function NoAccessPanel({ what = 'these responses' }) {
  return (
    <div className="uni-note uni-note--lock">
      <span aria-hidden="true">🔒</span>
      <span>
        You don't have permission to view {what}. Ask the owner to share it
        with you, or an administrator to grant access.
      </span>
    </div>
  );
}


/** "⬇ Export CSV" button — downloads through the authenticated client. */
function ExportButton({ path, filename, disabled }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadFile(path, filename);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="ty-btn ty-btn--secondary"
              onClick={go} disabled={busy || disabled}>
        {busy ? 'Preparing…' : '⬇ Export CSV'}
      </button>
      {error && (
        <span className="uni-field__hint" style={{ color: 'var(--ty-danger)' }}>
          {String(error.message || error)}
        </span>
      )}
    </>
  );
}


const LAYOUT_QUESTION_TYPES = new Set(['section_header', 'page_break', 'html']);
const PRESERVED_QUESTION_TYPES = new Set([
  'respondent_identity', 'file_upload', 'multi_file', 'signature',
]);


/** Badge shown on every admin-rewritten submission so they can be traced. */
function AdminEditedMark({ row, compact }) {
  const tz = useOrgTimeZone();
  if (!row?.admin_edited) return null;
  const who = row.admin_edited_by_label;
  const when = row.admin_edited_at ? fmtDateTime(row.admin_edited_at, tz) : '';
  const title = [
    'Edited by admin',
    who && `by ${who}`,
    when && `on ${when}`,
  ].filter(Boolean).join(' ');
  return (
    <span className="uni-admin-edited" title={title}>
      <UniStatusPill value="edited by admin" palette="amber" />
      {!compact && (who || when) && (
        <span className="uni-admin-edited__meta">
          {who}{who && when ? ' · ' : ''}{when}
        </span>
      )}
    </span>
  );
}


const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;


/* ====================================================================
   Form responses
   ==================================================================== */

export function UniFormsFormResponses() {
  const { formId } = useParams();
  const tz = useOrgTimeZone();
  const [searchParams] = useSearchParams();
  const selfOnly = searchParams.get('self') === '1';
  const [showIncomplete, setShowIncomplete] = React.useState(false);
  const [onlyAdminEdited, setOnlyAdminEdited] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [open, setOpen] = React.useState(null);   // the row being viewed
  const [editing, setEditing] = React.useState(false);
  const [editPayload, setEditPayload] = React.useState(null);
  const [editAnswers, setEditAnswers] = React.useState({});
  const [editEmail, setEditEmail] = React.useState('');
  const [editErrors, setEditErrors] = React.useState({});
  const [editBusy, setEditBusy] = React.useState(false);
  const [editError, setEditError] = React.useState(null);

  // ``full=1`` brings every answer down with the row so opening the
  // detail drawer doesn't need a second request per click.  Response
  // volumes per page are small (DRF page size), so the extra payload is
  // cheaper than N round-trips.  ``self=1`` is the My Submissions
  // screen: force the nested list down to the caller's own rows even
  // when they could otherwise read every submission.
  const search = q.trim();
  const url = `${EP.FORMS}${formId}/responses/?full=1`
    + `&page=${page}`
    + `&page_size=${pageSize}`
    + (showIncomplete ? '&include_incomplete=1' : '')
    + (onlyAdminEdited ? '&admin_edited=1' : '')
    + (selfOnly ? '&self=1' : '')
    + (search ? `&search=${encodeURIComponent(search)}` : '');
  const { data, loading, error, forbidden, reload } = useResponses(url);
  const exportPath = `${EP.FORMS}${formId}/responses/export/`
    + (search ? `?search=${encodeURIComponent(search)}` : '');

  const onSearch = (val) => {
    if (val !== q) setPage(1);
    setQ(val);
  };
  const onToggleIncomplete = (checked) => {
    setShowIncomplete(checked);
    setPage(1);
  };
  const onToggleAdminEdited = (checked) => {
    setOnlyAdminEdited(checked);
    setPage(1);
  };
  const onPageSize = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const rows    = data?.results || [];
  const columns = data?.columns || [];
  const title   = data?.form_title || 'Form';
  const scopedToSelf = data?.scoped_to_self === true;
  const canAdminEdit = !scopedToSelf && data?.can_admin_edit_responses === true;

  const closeModal = () => {
    setOpen(null);
    setEditing(false);
    setEditPayload(null);
    setEditAnswers({});
    setEditEmail('');
    setEditErrors({});
    setEditError(null);
  };

  const startEdit = async (row) => {
    setOpen(row);
    setEditing(true);
    setEditBusy(true);
    setEditError(null);
    setEditErrors({});
    try {
      const payload = await getJSON(
        `${EP.FORMS}${formId}/responses/${row.id}/admin-edit/`,
      );
      setEditPayload(payload);
      setEditAnswers(payload.answers || {});
      setEditEmail(payload.respondent_email || '');
    } catch (err) {
      setEditError(err);
    } finally {
      setEditBusy(false);
    }
  };

  const setEditAnswer = React.useCallback((qid, value) => {
    setEditAnswers((prev) => ({ ...prev, [qid]: value }));
    setEditErrors((prev) => {
      if (!prev[qid]) return prev;
      const next = { ...prev };
      delete next[qid];
      return next;
    });
  }, []);

  useLiveCalculations(
    editing ? (editPayload?.questions || []) : [],
    editAnswers,
    setEditAnswers,
    { recomputeOnMount: false },
  );

  const saveEdit = async () => {
    if (!open) return;
    setEditBusy(true);
    setEditError(null);
    setEditErrors({});
    try {
      const saved = await postJSON(
        `${EP.FORMS}${formId}/responses/${open.id}/admin-edit/`,
        { answers: editAnswers, respondent_email: editEmail },
      );
      setOpen(saved);
      setEditing(false);
      setEditPayload(null);
      reload();
    } catch (err) {
      const fieldErrors = err?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        setEditErrors(fieldErrors);
      }
      setEditError(err);
    } finally {
      setEditBusy(false);
    }
  };

  /** The answer for a given question on a given row, or ''. */
  const answerFor = (row, questionId) => {
    const hit = (row.answers || []).find((a) => String(a.question) === String(questionId));
    return hit ? hit.display_value : '';
  };

  const tableColumns = [
    { key: 'submitted_at', label: 'Submitted',
      render: (r) => fmtDateTime(r.submitted_at, tz) },
    ...(scopedToSelf ? [] : [{
      key: 'respondent_label', label: 'Respondent',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>{r.respondent_label || 'Anonymous'}</span>
      ),
    }]),
    { key: 'admin_edited', label: 'Admin edit',
      render: (r) => (r.admin_edited
        ? <AdminEditedMark row={r} compact />
        : <span className="uni-num--muted">—</span>) },
    // Only worth a column when the caller asked to see drafts too.
    ...(showIncomplete ? [{
      key: 'is_complete', label: 'Complete?', align: 'center',
      render: (r) => <UniBool value={r.is_complete} />,
    }] : []),
    // One column per question.  Truncated in the cell — the full text is
    // one click away in the drawer, and un-truncated paragraph answers
    // make the table unreadable.
    ...columns.map((c) => ({
      key: `q-${c.id}`,
      label: c.text,
      render: (r) => {
        const v = answerFor(r, c.id);
        if (!v) return <span className="uni-num--muted">—</span>;
        const short = v.length > 60 ? `${v.slice(0, 60)}…` : v;
        return <span title={v.length > 60 ? v : undefined}>{short}</span>;
      },
    })),
    { key: 'actions', label: '', align: 'right',
      render: (r) => (
        <span className="uni-row-actions">
          <button type="button" className="uni-row-btn" onClick={() => {
            setEditing(false);
            setEditPayload(null);
            setOpen(r);
          }}>
            View
          </button>
          {canAdminEdit && (
            <button type="button" className="uni-row-btn" onClick={() => startEdit(r)}>
              Edit
            </button>
          )}
        </span>
      ) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="📥"
      title={scopedToSelf ? `My responses — ${title}` : `Responses — ${title}`}
      subtitle={data?.count != null
        ? scopedToSelf
          ? `${fmtCount(data.count)} of your submission${data.count === 1 ? '' : 's'}${search ? ' matching search' : ''}`
          : `${fmtCount(data.count)} submission${data.count === 1 ? '' : 's'}${search ? ' matching search' : ''}`
        : scopedToSelf ? 'Your submissions to this form' : 'Submissions for this form'}
      actions={
        <>
          <UniActionButton
            hero
            to={selfOnly
              ? ROUTES.UNI_FORMS_MY_SUBMISSIONS
              : (scopedToSelf ? ROUTES.UNI_FORMS_FOR_ME : ROUTES.UNI_FORMS_FORMS)}
          >
            {selfOnly ? '← My Submissions' : (scopedToSelf ? '← For Me' : '← Forms')}
          </UniActionButton>
          {!scopedToSelf && (
            <UniActionButton hero to="/uni-forms/reports">📈 Reports</UniActionButton>
          )}
        </>
      }
    >
      {forbidden ? <NoAccessPanel what="this form's responses" /> : (
        <>
          <UniToolbar
            search={q}
            onSearch={onSearch}
            placeholder="Search respondents or answers…"
          >
            <label className="uni-check-field" style={{ margin: 0 }}>
              <input
                type="checkbox"
                checked={showIncomplete}
                onChange={(e) => onToggleIncomplete(e.target.checked)}
              />
              <span>
                <span className="uni-check-field__label">
                  Include partial submissions
                </span>
              </span>
            </label>
            {!scopedToSelf && (
              <label className="uni-check-field" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={onlyAdminEdited}
                  onChange={(e) => onToggleAdminEdited(e.target.checked)}
                />
                <span>
                  <span className="uni-check-field__label">
                    Only admin-edited
                  </span>
                </span>
              </label>
            )}
            {!scopedToSelf && (
              <ExportButton
                path={exportPath}
                filename={`${title}-responses.csv`}
                disabled={loading}
              />
            )}
          </UniToolbar>

          <UniTable
            rows={rows} loading={loading} error={error}
            columns={tableColumns} rowKey="id"
            empty={search
              ? 'No matching submissions'
              : (scopedToSelf ? 'No submissions yet' : 'No responses yet')}
            emptyHint={search
              ? 'Try a different name, email, or answer.'
              : scopedToSelf
                ? 'Fill in this form — your answers will show up here.'
                : onlyAdminEdited
                  ? 'No submissions have been edited by a form admin yet.'
                  : 'Share the form\'s link to start collecting submissions — they\'ll show up here.'}
          />
          {!forbidden && !error && (
            <UniPager
              page={page}
              pageSize={pageSize}
              count={data?.count}
              onPage={setPage}
              onPageSize={onPageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              disabled={loading}
            />
          )}
        </>
      )}

      {/* Answer sheet for one submission — view or admin-edit. */}
      <UniModal
        open={open !== null}
        title={editing ? 'Edit response (admin)' : 'Response detail'}
        onClose={closeModal}
        width={editing ? 720 : 640}
        footer={
          editing ? (
            <>
              <button type="button" className="ty-btn ty-btn--secondary"
                      onClick={() => { setEditing(false); setEditPayload(null); }}
                      disabled={editBusy}>
                Cancel
              </button>
              <button type="button" className="ty-btn"
                      onClick={saveEdit} disabled={editBusy || !editPayload}>
                {editBusy ? 'Saving…' : 'Save admin edits'}
              </button>
            </>
          ) : (
            <>
              {canAdminEdit && open && (
                <button type="button" className="ty-btn ty-btn--secondary"
                        onClick={() => startEdit(open)}>
                  Edit as admin
                </button>
              )}
              <button type="button" className="ty-btn ty-btn--secondary"
                      onClick={closeModal}>
                Close
              </button>
            </>
          )
        }
      >
        {open && !editing && (
          <>
            <UniDetailRow label="Respondent">
              {open.respondent_label || 'Anonymous'}
            </UniDetailRow>
            <UniDetailRow label="Email">{open.respondent_email}</UniDetailRow>
            <UniDetailRow label="Submitted">
              {fmtDateTime(open.submitted_at, tz)}
            </UniDetailRow>
            <UniDetailRow label="Complete">
              <UniBool value={open.is_complete} />
            </UniDetailRow>
            {open.admin_edited && (
              <UniDetailRow label="Admin edit">
                <AdminEditedMark row={open} />
              </UniDetailRow>
            )}
            {open.max_possible_score > 0 && (
              <UniDetailRow label="Score">
                {open.total_score} / {open.max_possible_score}
              </UniDetailRow>
            )}
            {open.personality_trait && (
              <UniDetailRow label="Personality">
                {open.personality_trait}
              </UniDetailRow>
            )}

            <UniSection title="Answers" />
            {(open.answers || []).length === 0 ? (
              <div className="uni-state">
                <div className="uni-state__title">No answers recorded.</div>
              </div>
            ) : open.answers.map((a) => (
              <UniDetailRow key={a.id} label={a.question_text || '—'}>
                {a.file_url
                  ? <a href={a.file_url} target="_blank" rel="noreferrer">
                      {a.display_value || 'Download file'}
                    </a>
                  : a.display_value}
              </UniDetailRow>
            ))}

            {/* Cross-module figures snapshotted at submit time. */}
            {(open.figures || []).length > 0 && (
              <>
                <UniSection title="Module figures" />
                {open.figures.map((f) => (
                  <UniDetailRow key={f.id} label={f.label || f.metric}>
                    {f.value}
                    {f.was_overridden && (
                      <span className="uni-field__hint">
                        Overridden (source was {f.source_value})
                        {f.override_reason ? ` — ${f.override_reason}` : ''}
                      </span>
                    )}
                  </UniDetailRow>
                ))}
              </>
            )}
          </>
        )}

        {open && editing && (
          <div className="uf-public uf-admin-edit">
            <div className="uni-admin-edit-banner">
              You are editing this submission as a form admin. The original
              respondent is unchanged. Saving marks this response as
              <strong> edited by admin</strong> so it can be traced.
            </div>
            <UniFormError error={editError} />
            {editBusy && !editPayload ? (
              <div className="uni-state">
                <div className="ty-spinner" aria-hidden="true" />
                <div className="uni-state__hint">Loading answers…</div>
              </div>
            ) : editPayload && (
              <>
                <UniDetailRow label="Respondent">
                  {editPayload.respondent_label || open.respondent_label || 'Anonymous'}
                </UniDetailRow>
                {editPayload.collect_email && (
                  <div className="uf-card">
                    <div className="uf-q-text">
                      Email address<span className="uf-req">*</span>
                    </div>
                    <input
                      type="email"
                      className={`uf-input${editErrors.respondent_email ? ' uf-input--invalid' : ''}`}
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                    {editErrors.respondent_email && (
                      <div className="uf-error">{editErrors.respondent_email}</div>
                    )}
                  </div>
                )}
                {(editPayload.questions || []).map((q) => {
                  if (LAYOUT_QUESTION_TYPES.has(q.question_type)) {
                    if (q.question_type === 'section_header') {
                      return (
                        <div className="uf-card" key={q.id}>
                          <div className="uf-section-title">{q.text}</div>
                          {q.description && <div className="uf-q-desc">{q.description}</div>}
                        </div>
                      );
                    }
                    return null;
                  }
                  if (PRESERVED_QUESTION_TYPES.has(q.question_type)) {
                    const stored = (open.answers || []).find(
                      (a) => String(a.question) === String(q.id),
                    );
                    return (
                      <div className="uf-card" key={q.id}>
                        <div className="uf-q-text">{q.text}</div>
                        <div className="uf-q-desc">
                          {stored?.display_value || 'This field cannot be changed by an admin edit.'}
                        </div>
                      </div>
                    );
                  }
                  const qid = String(q.id);
                  return (
                    <div className="uf-card" key={q.id}>
                      <div className="uf-q-text">
                        {q.text}
                        {q.is_required && <span className="uf-req">*</span>}
                      </div>
                      {q.description && <div className="uf-q-desc">{q.description}</div>}
                      <Field
                        q={q}
                        value={editAnswers[qid]}
                        onChange={setEditAnswer}
                        invalid={!!editErrors[qid]}
                      />
                      {editErrors[qid] && (
                        <div className="uf-error">{editErrors[qid]}</div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </UniModal>
    </UniPageShell>
  );
}


/* ====================================================================
   Quiz responses
   ==================================================================== */

export function UniFormsQuizResponses() {
  const { quizId } = useParams();
  const tz = useOrgTimeZone();
  const url = `${EP.QUIZZES}${quizId}/responses/`;
  const { data, loading, error, forbidden } = useResponses(url);

  const rows    = data?.results || [];
  const summary = data?.summary || {};
  const title   = data?.quiz_title || 'Quiz';

  const columns = [
    { key: 'submitted_at', label: 'Submitted',
      render: (r) => fmtDateTime(r.submitted_at, tz) },
    { key: 'respondent_label', label: 'Respondent',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>{r.respondent_label || 'Anonymous'}</span>
      ) },
    { key: 'total_score', label: 'Score', align: 'right',
      render: (r) => (
        <span className="uni-num">
          {r.total_score}
          <span className="uni-num--muted"> / {r.max_possible_score}</span>
        </span>
      ) },
    { key: 'percentage', label: '%', align: 'right',
      render: (r) => <span className="uni-num">{Number(r.percentage || 0).toFixed(1)}%</span> },
    { key: 'passed', label: 'Result', align: 'center',
      render: (r) => (r.passed === null || r.passed === undefined
        ? <span className="uni-num--muted">—</span>
        : <UniStatusPill value={r.passed ? 'passed' : 'failed'}
                         palette={r.passed ? 'green' : 'red'} />) },
    { key: 'outcome_title', label: 'Outcome',
      render: (r) => r.outcome_title || <span className="uni-num--muted">—</span> },
    { key: 'time_taken_seconds', label: 'Time', align: 'right',
      render: (r) => (r.time_taken_seconds
        ? `${Math.round(r.time_taken_seconds / 60)}m`
        : <span className="uni-num--muted">—</span>) },
  ];

  return (
    <UniPageShell
      theme={THEME}
      icon="📥"
      title={`Attempts — ${title}`}
      subtitle={data?.count != null
        ? `${fmtCount(data.count)} attempt${data.count === 1 ? '' : 's'}`
        : 'Attempts at this quiz'}
      actions={<UniActionButton hero to="/uni-forms/quizzes">← Quizzes</UniActionButton>}
    >
      {forbidden ? <NoAccessPanel what="this quiz's attempts" /> : (
        <>
          <div className="uni-kpis">
            <UniKpiCard icon="🧾" label="Attempts" value={fmtCount(summary.total)} />
            <UniKpiCard icon="✅" label="Passed"   value={fmtCount(summary.passed)}
                        sublabel={`pass mark ${summary.pass_percentage ?? '—'}%`} />
            <UniKpiCard icon="❌" label="Failed"   value={fmtCount(summary.failed)} />
            <UniKpiCard icon="📊" label="Average"
                        value={summary.avg_percentage != null
                          ? `${summary.avg_percentage}%` : '—'} />
          </div>

          <UniToolbar>
            <ExportButton
              path={`${EP.QUIZZES}${quizId}/responses/export/`}
              filename={`${title}-attempts.csv`}
              disabled={loading}
            />
          </UniToolbar>

          <UniTable
            rows={rows} loading={loading} error={error}
            columns={columns} rowKey="id"
            empty="No attempts yet"
            emptyHint="Publish the quiz and share its link — scored attempts will appear here."
          />
        </>
      )}
    </UniPageShell>
  );
}


/* ====================================================================
   Poll results
   ==================================================================== */

export function UniFormsPollResults() {
  const { pollId } = useParams();
  const url = `${EP.POLLS}${pollId}/results/`;
  const { data, loading, error, forbidden } = useResponses(url);

  const options = data?.options || [];
  const total   = data?.total_votes || 0;
  const title   = data?.poll_title || 'Poll';

  return (
    <UniPageShell
      theme={THEME}
      icon="📊"
      title={`Results — ${title}`}
      subtitle={data
        ? `${fmtCount(total)} vote${total === 1 ? '' : 's'}`
        : 'Live poll results'}
      actions={<UniActionButton hero to="/uni-forms/polls">← Polls</UniActionButton>}
    >
      {forbidden ? <NoAccessPanel what="this poll's results" /> : loading ? (
        <div className="uni-state">
          <div className="ty-spinner" aria-hidden="true" />
          <div className="uni-state__hint">Loading…</div>
        </div>
      ) : error ? (
        <div className="ty-error">
          <div className="ty-error__head">
            <span className="ty-error__icon">⚠️</span>
            Couldn't load the results.
          </div>
          <div className="ty-error__trail">{String(error.message || error)}</div>
        </div>
      ) : (
        <>
          <UniToolbar>
            <ExportButton
              path={`${EP.POLLS}${pollId}/results/export/`}
              filename={`${title}-results.csv`}
            />
          </UniToolbar>

          <div className="uni-card">
            <div className="uni-card__head">
              <div>
                <h3 className="uni-card__title">{data?.question || title}</h3>
                <div className="uni-card__sub">
                  {fmtCount(total)} vote{total === 1 ? '' : 's'} cast
                </div>
              </div>
            </div>

            {options.length === 0 ? (
              <div className="uni-state">
                <div className="uni-state__icon">📭</div>
                <div className="uni-state__title">This poll has no options yet.</div>
                <div className="uni-state__hint">
                  Add the choices voters should pick from, then publish the poll.
                </div>
              </div>
            ) : options.map((o) => (
              <div className="uni-bar-row" key={o.id}>
                <div className="uni-bar-row__head">
                  <span className="uni-bar-row__label">{o.text}</span>
                  <span className="uni-bar-row__value">
                    {fmtCount(o.votes)} · {o.percentage}%
                  </span>
                </div>
                <div className="uni-bar">
                  <div
                    className="uni-bar__fill"
                    style={{
                      width: `${o.percentage}%`,
                      // Respect a per-option colour when the author set one.
                      background: o.color || undefined,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Free-text values behind an "Other" option — invisible in the
              bars, and usually the most interesting part of the answer. */}
          {(data?.other_answers || []).length > 0 && (
            <>
              <UniSection title="“Other” answers" />
              <div className="uni-card">
                {data.other_answers.map((text, i) => (
                  <UniDetailRow key={i} label={`#${i + 1}`}>{text}</UniDetailRow>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </UniPageShell>
  );
}
